import { PIAPIVersion } from "@/apiConfig";
import { createRpcBackend } from "@/backend/backendProvider";
import { streamExportXlsx } from "@/backend/streamExportXlsx";
import { sortStickies } from "@/components/session/boardUtils";
import { piserverApiUrl } from "@/environment";
import { useToastStore } from "@/stores/toast";
import { generateId } from "@/utils";
import { colorOrFallback, rgbToHex } from "@/utils/color";
import { dateToIsoDay, isoDayToInputDate, unixTimestampToDate } from "@/utils/dates";

export default class SessionHandler {
  constructor(context, sessionId) {
    this.sessionId = sessionId;
    this.backend = createRpcBackend(context);
  }

  delete() {
    return this.backend.sessionCall("delete", [this.sessionId]);
  }

  async getXlsx() {
    const url = `${piserverApiUrl}/ch/rentouch/piplanning/${PIAPIVersion}/session/get_xlsx/${this.sessionId}/`;
    const res = await fetch(url, {
      credentials: "include",
      headers: { Accept: "application/x-ndjson" },
    });
    if (!res.ok) throw new Error(`export failed: ${res.status}`);
    return streamExportXlsx(res.body);
  }

  importFile(method, dataBase64) {
    return this.backend.sessionCall(method, [this.sessionId, dataBase64]);
  }

  getName() {
    return this.backend.sessionCall("get_name", [this.sessionId]);
  }

  setName(name) {
    return this.backend.sessionCall("set_name", [this.sessionId, name]);
  }

  async getPlanningDates() {
    const dates = await this.backend.sessionCall("get_planning_dates", [this.sessionId]);

    return {
      start: isoDayToInputDate(dates?.start),
      end: isoDayToInputDate(dates?.end),
    };
  }

  setPlanningDates(start, end) {
    return this.backend.sessionCall("set_planning_dates", [
      this.sessionId,
      dateToIsoDay(start),
      dateToIsoDay(end),
    ]);
  }

  getSettings() {
    return this.backend.sessionCall("get_settings", [this.sessionId]);
  }

  async setSettings(settings) {
    for (const [key, value] of Object.entries(settings)) {
      // A setting without a value is left as it is on the backend.
      if (value === undefined) continue;
      await this.backend.sessionCall("set_settings", [this.sessionId, key, value]);
    }
  }

  getStickerTypes() {
    return this.backend.sessionCall("get_sticker_types", [this.sessionId]);
  }

  setStickerTypes(stickerTypes) {
    return this.backend.sessionCall("set_sticker_types", [this.sessionId, stickerTypes]);
  }

  getBoards() {
    return this.backend.sessionCall("get_boards", [this.sessionId]);
  }

  searchStickies(query) {
    return this.backend.sessionCall("search_stickies", [this.sessionId, query]);
  }

  getParticipatingTeamIds() {
    return this.backend.sessionCall("get_participating_users", [this.sessionId]);
  }

  setParticipatingTeamIds(teamIds) {
    return this.backend.sessionCall("set_participating_users", [this.sessionId, teamIds]);
  }

  getTeams() {
    return this.backend.sessionCall("get_teams", [this.sessionId]);
  }

  getArts() {
    return this.backend.sessionCall("get_arts", [this.sessionId]);
  }

  getStickyTypes() {
    return this.backend.sessionCall("get_sticky_types", [this.sessionId]);
  }

  getIterations() {
    return this.backend.sessionCall("get_iterations", [this.sessionId]);
  }

  setIterations(iterations) {
    return this.backend.sessionCall("set_iterations", [this.sessionId, iterations]);
  }

  updateSwimlanes(edits) {
    return this.backend.sessionCall("update_swimlanes", [this.sessionId, edits]);
  }

  getLinkTypes() {
    return this.backend.sessionCall("get_link_types", [this.sessionId]);
  }

  getFlexTypes() {
    return this.backend.sessionCall("get_flexboard_types", [this.sessionId]);
  }

  getFlexBackgrounds() {
    return this.backend.sessionCall("get_flexboard_backgrounds", []);
  }

  createFlexType(name, background) {
    return this.backend.sessionCall("create_flexboard_type", [this.sessionId, name, background]);
  }

  archiveSession(sessionId) {
    return this.backend.sessionCall("archive", [sessionId]);
  }

  unarchiveSession(sessionId) {
    return this.backend.sessionCall("unarchive", [sessionId]);
  }

  updateFlexType(id, name, background) {
    return this.backend.sessionCall("update_flexboard_type", [id, name, background]);
  }

  deleteFlexType(id) {
    return this.backend.sessionCall("delete_flexboard_type", [id]);
  }

  hasStickiesOfType(id) {
    // session id threaded for the NATS subject (WAMP wire keeps [id] only).
    return this.backend.sessionCall("has_stickies_of_type", [id], undefined, this.sessionId);
  }

  createLinkType(fromId, toId) {
    return this.backend.sessionCall("create_link_type", [this.sessionId, fromId, toId]);
  }

  updateLinkType(id, fromId, toId) {
    return this.backend.sessionCall(
      "alter_link_type",
      [
        id,
        {
          from_sticky_type: fromId,
          to_sticky_type: toId,
        },
      ],
      undefined,
      this.sessionId,
    );
  }

  deleteLinkType(id) {
    // session id threaded for the NATS subject (WAMP wire keeps [id] only).
    return this.backend.sessionCall("delete_link_type", [id], undefined, this.sessionId);
  }

  async loadStickyTypes(boardType, almHandler) {
    const types = await this.getStickyTypes();
    const stickies = types.filter((sticky) => sticky.available_board_types.includes(boardType));
    const loader = await this.stickyLoaders[boardType](almHandler);
    stickies.forEach((sticky) => loader(sticky));
    return sortStickies(stickies, boardType);
  }

  async loadIterations() {
    const iterations = await this.getIterations();
    return iterations.map((iter) => ({
      id: iter.id,
      name: iter.name,
      start: unixTimestampToDate(iter.start),
      end: unixTimestampToDate(iter.end),
      teams: [],
      nameError: "",
      dateError: "",
    }));
  }

  async loadFlexBoards() {
    const stickies = await this.getStickyTypes();
    const boards = await this.getFlexTypes();
    return boards.map((board) => ({
      id: board.id,
      name: board.name,
      background: board.background,
      error: null,
      stickyTypes: stickies
        .filter((s) => s.origin_board_type === "flex" && s.origin_flexboard_type === board.id)
        .map((s) => {
          return {
            id: s.id,
            name: s.name,
            color: rgbToHex(s.color[0], s.color[1], s.color[2]),
            index: s.index,
            frontend_id: generateId(),
            error: "",
            functionality: s.functionality,
          };
        })
        .sort((a, b) => a.index - b.index),
    }));
  }

  saveStickyType(sticky, data) {
    if ("id" in sticky) {
      return this.updateStickyType(sticky.id, data);
    }
    return this.createStickyType(data).then((id) => (sticky.id = id));
  }

  createStickyType(sticky, almType) {
    return this.backend.sessionCall("create_sticky_type", [
      this.sessionId,
      sticky.name,
      sticky.color,
      sticky.origin_board_type,
      almType,
      sticky,
    ]);
  }

  updateStickyType(id, data) {
    // Preserve the pre-NATS `callThrowing` semantic: callers (boardMixin.saveSticky,
    // FlexBoards.updateSticky) await this and rely on a rejection to surface failures.
    // Without `throwing`, the WAMP fallback resolves on `{success:false}` and swallows them.
    return this.backend.sessionCall("update_sticky_type", [id, data], undefined, undefined, {
      throwing: true,
    });
  }

  deleteUnusedStickyType(id) {
    return this.backend.sessionCall("delete_unused_sticky_type", [id]);
  }

  stickyLoaders = {
    program: () => this.baseStickyLoader("program"),
    solution: () => this.baseStickyLoader("solution"),
    risk: () => this.baseStickyLoader("risk"),
    team: async (almHandler) => {
      const isAlm = almHandler && almHandler.getAlmTypes;
      const almTypes = isAlm ? await almHandler.getAlmTypes() : [];
      return this.baseStickyLoader("team", (sticky) => {
        if (sticky.functionality === "dependency") {
          sticky.alt_color = sticky.alt_color
            ? rgbToHex(sticky.alt_color[0], sticky.alt_color[1], sticky.alt_color[2])
            : sticky.color;
        }
        Object.assign(sticky, {
          issueType: -1,
          serverData: [],
          availableIssueFields: [],
          checking: false,
          hasNameError: false,
          hasServerError: false,
          hasWarning: false,
          couldntVerify: false,
        });
        if (isAlm) {
          const almType = almTypes.find((almType) => almType.id === sticky.alm_type);
          if (!almType) {
            this.showMissingAlmTypeMessage(sticky, "team");
            return;
          }
          Object.assign(sticky, almHandler.getTeamBoardAlmType(almType));
        }
      });
    },
    backlog: async (almHandler) => {
      const isAlm = almHandler && almHandler.getAlmTypes;
      const almTypes = isAlm ? await almHandler.getAlmTypes() : [];
      return this.baseStickyLoader("backlog", (sticky) => {
        if (sticky.functionality === "workitem") {
          sticky.alt_color = sticky.alt_color
            ? rgbToHex(sticky.alt_color[0], sticky.alt_color[1], sticky.alt_color[2])
            : sticky.color;
        }
        Object.assign(sticky, {
          project: -1,
          issueType: -1,
          selectedField: -1,
          availableIssueTypes: [],
          availableIssueFields: {},
          availableReleases: [],
          labels: "",
          tags: [],
          release: -1,
          adoFields: [],
          hasNameError: false,
          hasIssueError: false,
          issueLoad: false,
          fieldLoad: false,
        });
        if (isAlm) {
          const almType = almTypes.find((almType) => almType.id === sticky.alm_type);
          if (!almType) {
            this.showMissingAlmTypeMessage(sticky, "art-backlog");
            return;
          }
          Object.assign(sticky, {
            project: almType.project_id || -1,
            issueType: almType.issue_type,
            ...almHandler.getBacklogBoardAlmType(sticky, almType),
          });
        }
      });
    },
    solution_backlog: async (almHandler) => {
      const isAlm = almHandler && almHandler.getAlmTypes;
      const almTypes = isAlm ? await almHandler.getAlmTypes() : [];
      return this.baseStickyLoader("solution_backlog", (sticky) => {
        if (sticky.functionality === "workitem") {
          sticky.alt_color = sticky.alt_color
            ? rgbToHex(sticky.alt_color[0], sticky.alt_color[1], sticky.alt_color[2])
            : sticky.color;
        }
        Object.assign(sticky, {
          project: -1,
          issueType: -1,
          selectedField: -1,
          availableIssueTypes: [],
          availableIssueFields: {},
          availableReleases: [],
          labels: "",
          tags: [],
          release: -1,
          adoFields: [],
          hasNameError: false,
          hasIssueError: false,
          issueLoad: false,
          fieldLoad: false,
        });
        if (isAlm) {
          const almType = almTypes.find((almType) => almType.id === sticky.alm_type);
          if (!almType) {
            this.showMissingAlmTypeMessage(sticky, "solution-backlog");
            return;
          }
          Object.assign(sticky, {
            project: almType.project_id || -1,
            issueType: almType.issue_type,
            ...almHandler.getBacklogBoardAlmType(sticky, almType),
          });
        }
      });
    },
  };

  baseStickyLoader(boardType, process) {
    return (sticky) => {
      this.initSticky(sticky);
      sticky.frontend_id = sticky.id;
      sticky.color = colorOrFallback(sticky.color);
      sticky.color = rgbToHex(sticky.color[0], sticky.color[1], sticky.color[2]);
      if (sticky.origin_board_type !== boardType) {
        sticky.disabled_editing = true;
      }
      process?.(sticky);
      return sticky;
    };
  }

  initSticky(sticky) {
    const originalTypes = [...(sticky.available_board_types || [])];
    const origin = sticky.origin_board_type;
    sticky.available_board_types = sticky.available_board_types || [];
    if (!sticky.available_board_types.includes(origin)) {
      sticky.available_board_types.push(origin);
    }
    let i = 0;
    while (i < sticky.available_board_types.length) {
      if (sticky.available_board_types[i].trim() === "") {
        sticky.available_board_types.splice(i, 1);
      } else {
        i++;
      }
    }
    const changed =
      originalTypes.length !== sticky.available_board_types.length ||
      originalTypes.some((t, idx) => t !== sticky.available_board_types[idx]);
    if (changed) {
      this.updateStickyType(sticky.id, {
        available_board_types: sticky.available_board_types,
      });
    }
  }

  /**
   * Show a message when the ALM type is missing for a sticky.
   * This is an edge case, but at least offers the admin a way to
   * fix the issue (until we can figure out why it's missing in the first place).
   * See https://rentouch.atlassian.net/browse/REN-14106
   */
  showMissingAlmTypeMessage(sticky, boardLevel) {
    const toastStore = useToastStore();
    toastStore.showMessage({
      message: `ALM Mapping not found for the "${sticky.name}" ${boardLevel}-sticky type (id: ${sticky.id})`,
      type: "error",
    });
  }
}
